import React, { createContext, useContext, useEffect, useReducer } from "react";
import { toast } from "sonner";
import { uid } from "@/lib/format";
import { normalizeLanguage, setRuntimeLanguage } from "@/i18n/core";
import { localizeCatalogSource, translateKey } from "@/i18n/registry";
import {
  dutyIssueMessage,
  findAvailableSlots,
  findBookingConflictDetails,
  findDutyAssignmentIssue,
} from "@/lib/scheduling";
import { createInitialSegments, refreshSegments } from "@/lib/segmentation";
import {
  CLINIC, USER, TEAM, RESOURCES, SERVICES, ROOM_DUTIES, CHANNELS, CUSTOMERS, BOOKINGS,
  CONVERSATIONS, AUTOMATIONS, ACTIVATION, ACTIVITY, STAGES, CLOSED_STAGES,
} from "@/data/seed";

const STORAGE_KEY = "askilia-segmentation-state-v1";
const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value || {}, key);
const CURRENT_BOOKING_SEED_VERSION = BOOKINGS.reduce(
  (version, booking) => Math.max(version, Number(booking.seedVersion) || 0),
  0,
);

function snapshotBookingAssignments(booking, services = [], resources = [], seededBooking = null) {
  if (!booking) return booking;
  const resourceById = new Map(resources.map((resource) => [resource.id, resource]));
  const service = services.find((item) => item.id === booking.serviceId);
  const explicitIds = Array.isArray(booking.resourceIds) ? booking.resourceIds.filter(Boolean) : [];
  const typedId = (type) => explicitIds.find((id) => resourceById.get(id)?.type === type) || null;
  const legacyResource = resourceById.get(booking.resourceId);
  const serviceResource = resourceById.get(service?.resource);
  const serviceRoom = resourceById.get(service?.room);
  const supportedAssignment = (id, type) => (
    id && resourceById.get(id)?.type === type ? id : null
  );

  const doctorId = hasOwn(booking, "doctorId")
    ? supportedAssignment(booking.doctorId, "person")
    : typedId("person")
      || (legacyResource?.type === "person" ? booking.resourceId : null)
      || supportedAssignment(seededBooking?.doctorId, "person")
      || (serviceResource?.type === "person" ? service.resource : null)
      || null;
  const roomId = hasOwn(booking, "roomId")
    ? supportedAssignment(booking.roomId, "room")
    : typedId("room")
      || (legacyResource?.type === "room" ? booking.resourceId : null)
      || supportedAssignment(seededBooking?.roomId, "room")
      || (serviceRoom?.type === "room" ? service.room : null)
      || null;
  const extraResourceIds = Array.from(new Set([...explicitIds, booking.resourceId]
    .filter((id) => {
      const resource = resourceById.get(id);
      return resource && resource.type !== "person" && resource.type !== "room";
    })));
  const resourceIds = Array.from(new Set([doctorId, roomId, ...extraResourceIds].filter(Boolean)));

  return {
    ...booking,
    resourceId: doctorId || roomId || extraResourceIds[0] || null,
    doctorId,
    roomId,
    resourceIds,
  };
}

function conflictDescription(details, resources = [], language = "en") {
  const resourceById = new Map(resources.map((resource) => [resource.id, resource]));
  const names = Array.from(new Set(details.flatMap((detail) => detail.sharedResourceIds || [])))
    .map((id) => {
      const source = resourceById.get(id)?.short || resourceById.get(id)?.name;
      return source
        ? localizeCatalogSource(source, {}, language)
        : translateKey("schedule.resource.required", {}, language);
    });
  const subject = names.length
    ? names.join(", ")
    : translateKey("schedule.resource.requiredPersonOrRoom", {}, language);
  return translateKey(
    details.some((detail) => detail.type === "occupied")
      ? "schedule.conflict.occupied"
      : "schedule.conflict.buffer",
    { subject },
    language,
  );
}

function bookingAvailabilityIssue(state, candidate, excludeId = null) {
  if (!candidate) {
    return {
      type: "invalid",
      description: translateKey("schedule.issue.chooseValidTime", {}, state.lang),
    };
  }
  const conflicts = findBookingConflictDetails(state.bookings, state.services, candidate, excludeId);
  if (conflicts.length) {
    return {
      type: "booking_conflict",
      conflicts,
      description: conflictDescription(conflicts, state.resources, state.lang),
    };
  }
  const dutyIssue = findDutyAssignmentIssue(state.roomDuties || [], candidate);
  if (dutyIssue) {
    return {
      type: "duty",
      dutyIssue,
      description: dutyIssueMessage(dutyIssue, state.resources, state.lang),
    };
  }
  return null;
}

function freshOnboarding() {
  return {
    version: 1,
    mode: "self_serve",
    status: "not_started",
    startedAt: null,
    source: null,
    sourceConnectedAt: null,
    requestedConnector: null,
    skippedStepIds: [],
    websiteUrl: "",
    knowledgePages: [],
    importStartedAt: null,
    businessDataReadyAt: null,
    connectedChannelIds: [],
    inboxOpenedAt: null,
    reviewedAt: null,
  };
}

function freshIliaReleases() {
  const now = Date.now();
  return {
    activeId: "ilia-v1",
    stagingId: "ilia-v2",
    versions: [
      {
        id: "ilia-v2",
        number: 2,
        name: "Safer booking choices",
        summary: "Offers several available times and waits for explicit customer confirmation.",
        createdAt: new Date(now - 2 * 60 * 60 * 1000).toISOString(),
        testedAt: null,
        publishedAt: null,
      },
      {
        id: "ilia-v1",
        number: 1,
        name: "Clinic assistant baseline",
        summary: "Current prompt, qualification workflows and human handoff rules.",
        createdAt: new Date(now - 12 * 24 * 60 * 60 * 1000).toISOString(),
        testedAt: new Date(now - 8 * 24 * 60 * 60 * 1000).toISOString(),
        publishedAt: new Date(now - 7 * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        id: "ilia-v0",
        number: 0,
        name: "Initial setup",
        summary: "First imported prompt and default qualification workflow.",
        createdAt: new Date(now - 20 * 24 * 60 * 60 * 1000).toISOString(),
        testedAt: new Date(now - 18 * 24 * 60 * 60 * 1000).toISOString(),
        publishedAt: new Date(now - 17 * 24 * 60 * 60 * 1000).toISOString(),
        retiredAt: new Date(now - 7 * 24 * 60 * 60 * 1000).toISOString(),
      },
    ],
  };
}

function demoKnowledgePages(websiteUrl) {
  const base = websiteUrl.replace(/\/$/, "");
  return [
    {
      id: "kb-services",
      title: "Services and pricing",
      url: `${base}/services`,
      summary: "Consultations, diagnostics, current list prices and the practical next step for each service.",
      status: "prototype_preview",
    },
    {
      id: "kb-about",
      title: "About the clinic",
      url: `${base}/about`,
      summary: "Clinic positioning, languages, locations and the care experience customers can expect.",
      status: "prototype_preview",
    },
    {
      id: "kb-faq",
      title: "Frequently asked questions",
      url: `${base}/faq`,
      summary: "Preparation, appointment timing, payment and common policy questions.",
      status: "prototype_preview",
    },
    {
      id: "kb-contact",
      title: "Locations and contact",
      url: `${base}/contact`,
      summary: "Addresses, opening hours, phone numbers and arrival information.",
      status: "prototype_preview",
    },
  ];
}

function freshState() {
  const segmentSnapshotAt = new Date(Date.now() - 3 * 3600 * 1000).toISOString();
  const base = {
    lang: "en",
    clinic: CLINIC,
    user: USER,
    team: TEAM,
    resources: RESOURCES,
    services: SERVICES,
    roomDuties: ROOM_DUTIES,
    bookingSeedVersion: CURRENT_BOOKING_SEED_VERSION,
    roomDutySeedVersion: CURRENT_BOOKING_SEED_VERSION,
    channels: CHANNELS,
    customers: CUSTOMERS,
    bookings: BOOKINGS,
    conversations: CONVERSATIONS.map((conversation) => ({
      ...conversation,
      archivedAt: conversation.archivedAt ?? null,
      archivedBy: conversation.archivedBy ?? null,
    })),
    automations: AUTOMATIONS,
    iliaReleases: freshIliaReleases(),
    activation: ACTIVATION,
    onboarding: freshOnboarding(),
    activity: ACTIVITY,
    erp: {
      connected: true,
      name: "Medesk",
      lastSync: new Date(Date.now() - 3 * 3600 * 1000).toISOString(),
      autoSync: "off",
      pendingChanges: [
        { serviceId: "s-cardio", from: 120, to: 130 },
        { serviceId: "s-blood", from: 60, to: 65 },
      ],
    },
    segmentSync: {
      crm: "Medesk",
      connected: true,
      lastPullAt: segmentSnapshotAt,
      lastAnalysisAt: segmentSnapshotAt,
      recordsChecked: CUSTOMERS.length,
      conversationsChecked: CONVERSATIONS.length,
      pendingReview: CUSTOMERS.filter((customer) => customer.mergePending).length,
    },
  };
  return { ...base, segments: createInitialSegments(base, segmentSnapshotAt) };
}

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const stored = JSON.parse(raw);
      const seededCustomers = new Map(CUSTOMERS.map((customer) => [customer.id, customer]));
      const storedCustomers = stored.customers || [];
      const storedCustomerIds = new Set(storedCustomers.map((customer) => customer.id));
      const customers = [
        ...storedCustomers.map((customer) => {
          const seeded = seededCustomers.get(customer.id);
          const staleSalomeValue = customer.id === "cust-salome" && customer.value === 80 && seeded?.value === 120;
          return {
            ...customer,
            ageBand: customer.ageBand ?? seeded?.ageBand,
            value: staleSalomeValue ? seeded.value : customer.value,
            valueSource: customer.valueSource || seeded?.valueSource || "staff",
            valueBasis: customer.valueBasis || seeded?.valueBasis || customer.qualification?.[0]?.value || "Current opportunity",
            commercial: { ...(seeded?.commercial || {}), ...(customer.commercial || {}) },
          };
        }),
        ...CUSTOMERS.filter((customer) => !storedCustomerIds.has(customer.id)),
      ];
      const seededServices = new Map(SERVICES.map((service) => [service.id, service]));
      const services = (stored.services || SERVICES).map((service) => ({
        ...seededServices.get(service.id),
        ...service,
        description: service.description || seededServices.get(service.id)?.description || "",
      }));
      const seededResources = new Map(RESOURCES.map((resource) => [resource.id, resource]));
      const storedResources = stored.resources || [];
      const storedResourceIds = new Set(storedResources.map((resource) => resource.id));
      const resources = [
        ...storedResources.map((resource) => ({
          ...(seededResources.get(resource.id) || {}),
          ...resource,
        })),
        ...RESOURCES.filter((resource) => !storedResourceIds.has(resource.id)),
      ];
      const seededBookings = new Map(BOOKINGS.map((booking) => [booking.id, booking]));
      const storedBookings = stored.bookings || [];
      const storedBookingIds = new Set(storedBookings.map((booking) => booking.id));
      const bookings = [
        ...storedBookings.map((booking) => {
          const seeded = seededBookings.get(booking.id);
          const seedHasNewerVersion = Number(seeded?.seedVersion || 0) > Number(booking.seedVersion || 0);
          // Seed-owned demo rows are replaced as one snapshot. Merging an old
          // row into a new seed left stale assignment IDs behind and created
          // conflicts for doctors or rooms that were no longer assigned.
          const current = seedHasNewerVersion ? { ...seeded } : booking;
          return snapshotBookingAssignments({
            ...current,
            showUpProbability: current.showUpProbability ?? seeded?.showUpProbability,
            showUpSource: current.showUpSource || seeded?.showUpSource,
          }, services, resources, seeded);
        }),
        ...BOOKINGS
          .filter((booking) => !storedBookingIds.has(booking.id))
          .map((booking) => snapshotBookingAssignments(booking, services, resources, booking)),
      ];
      const seededConversations = new Map(CONVERSATIONS.map((conversation) => [conversation.id, conversation]));
      const storedConversations = stored.conversations || [];
      const storedConversationIds = new Set(storedConversations.map((conversation) => conversation.id));
      const conversations = [
        ...storedConversations.map((conversation) => ({
          ...(seededConversations.get(conversation.id) || {}),
          ...conversation,
          archivedAt: conversation.archivedAt ?? null,
          archivedBy: conversation.archivedBy ?? null,
        })),
        ...CONVERSATIONS
          .filter((conversation) => !storedConversationIds.has(conversation.id))
          .map((conversation) => ({
            ...conversation,
            archivedAt: conversation.archivedAt ?? null,
            archivedBy: conversation.archivedBy ?? null,
          })),
      ];
      const fresh = freshState();
      const storedDutyVersion = Number(stored.roomDutySeedVersion ?? stored.bookingSeedVersion ?? 0);
      const roomDuties = storedDutyVersion >= CURRENT_BOOKING_SEED_VERSION
        && Array.isArray(stored.roomDuties)
        ? stored.roomDuties
        : ROOM_DUTIES;
      const refreshedAt = stored.segmentSync?.lastAnalysisAt || fresh.segmentSync.lastAnalysisAt;
      const segments = refreshSegments({ customers, conversations, bookings }, stored.segments || fresh.segments, refreshedAt);
      return {
        ...fresh,
        ...stored,
        lang: normalizeLanguage(stored.lang),
        customers,
        resources,
        services,
        roomDuties,
        bookingSeedVersion: CURRENT_BOOKING_SEED_VERSION,
        roomDutySeedVersion: CURRENT_BOOKING_SEED_VERSION,
        bookings,
        conversations,
        onboarding: { ...fresh.onboarding, ...(stored.onboarding || {}) },
        iliaReleases: {
          ...fresh.iliaReleases,
          ...(stored.iliaReleases || {}),
          versions: stored.iliaReleases?.versions || fresh.iliaReleases.versions,
        },
        segments,
        segmentSync: {
          ...fresh.segmentSync,
          ...(stored.segmentSync || {}),
          recordsChecked: customers.length,
          conversationsChecked: conversations.length,
          pendingReview: customers.filter((customer) => customer.mergePending).length,
        },
      };
    }
  } catch (e) { /* ignore */ }
  return freshState();
}

function slotLabel(start) {
  return new Intl.DateTimeFormat("en", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(start));
}

function logEntry(actor, action, detail, entity, extra = {}) {
  return { id: uid("act"), ts: new Date().toISOString(), actor, action, detail, entity, undoable: false, undone: false, ...extra };
}

function reducer(state, action) {
  switch (action.type) {
    case "RESET":
      return { ...freshState(), lang: normalizeLanguage(state.lang) };

    case "SET_LANG":
      return { ...state, lang: normalizeLanguage(action.lang) };

    case "RESET_ONBOARDING":
      return {
        ...state,
        clinic: { ...state.clinic, website: CLINIC.website },
        channels: CHANNELS.map((channel) => ({ ...channel })),
        activation: ACTIVATION.map((item) => ({ ...item })),
        onboarding: freshOnboarding(),
        erp: {
          connected: true,
          name: "Medesk",
          lastSync: new Date(Date.now() - 3 * 3600 * 1000).toISOString(),
          autoSync: "off",
          pendingChanges: [
            { serviceId: "s-cardio", from: 120, to: 130 },
            { serviceId: "s-blood", from: 60, to: 65 },
          ],
        },
        activity: state.activity.filter((item) => item.entity !== "onboarding"),
      };

    case "CONNECT_ONBOARDING_SOURCE":
      return {
        ...state,
        onboarding: {
          ...state.onboarding,
          status: "in_progress",
          startedAt: state.onboarding.startedAt || action.at,
          source: action.source,
          sourceConnectedAt: action.at,
          importStartedAt: action.at,
          businessDataReadyAt: null,
          reviewedAt: null,
        },
        erp: {
          connected: true,
          name: action.source,
          lastSync: action.at,
          autoSync: state.erp?.autoSync || "off",
          pendingChanges: [],
        },
        activity: [logEntry("human", `Connected ${action.source}`, "Clinic management source authenticated during onboarding", "onboarding"), ...state.activity],
      };

    case "START_ONBOARDING_IMPORT":
      return {
        ...state,
        onboarding: {
          ...state.onboarding,
          status: "building",
          websiteUrl: action.websiteUrl,
          knowledgePages: demoKnowledgePages(action.websiteUrl),
          importStartedAt: state.onboarding.businessDataReadyAt
            ? action.at
            : state.onboarding.importStartedAt || action.at,
          businessDataReadyAt: null,
          reviewedAt: null,
        },
        clinic: { ...state.clinic, website: action.websiteUrl.replace(/^https?:\/\//, "").replace(/\/$/, "") },
      };

    case "CONNECT_ONBOARDING_CHANNEL": {
      const ids = Array.from(new Set([...(state.onboarding.connectedChannelIds || []), action.channelId]));
      const knownChannel = state.channels.some((channel) => channel.id === action.channelId);
      const channels = knownChannel
        ? state.channels.map((channel) => channel.id === action.channelId ? { ...channel, status: "connected" } : channel)
        : [...state.channels, {
            id: action.channelId,
            name: action.channelName,
            handle: action.channelHandle,
            status: "connected",
            color: "human",
          }];
      return {
        ...state,
        onboarding: {
          ...state.onboarding,
          status: state.onboarding.businessDataReadyAt ? "workspace_ready" : "building",
          connectedChannelIds: ids,
        },
        channels,
        activation: state.activation.map((item) => item.id === "act-1" ? { ...item, done: true } : item),
        activity: [logEntry("human", `Connected ${action.channelName}`, "Customer channel connected during onboarding", "onboarding"), ...state.activity],
      };
    }

    case "FINISH_ONBOARDING_IMPORT":
      if (state.onboarding.businessDataReadyAt) return state;
      return {
        ...state,
        onboarding: {
          ...state.onboarding,
          status: state.onboarding.connectedChannelIds?.length ? "workspace_ready" : "business_ready",
          businessDataReadyAt: action.at,
        },
        activation: state.activation.map((item) => ["act-2", "act-4"].includes(item.id) ? { ...item, done: true } : item),
        activity: [logEntry("automation", "Business data ready", `${state.services.length} services and ${state.resources.length} resources prepared`, "onboarding"), ...state.activity],
      };

    case "UPDATE_ONBOARDING":
      return { ...state, onboarding: { ...state.onboarding, ...action.patch } };

    case "CONNECT_CHANNEL":
      return {
        ...state,
        channels: state.channels.map((c) => (c.id === action.id ? { ...c, status: "connected" } : c)),
      };

    case "TOGGLE_AUTOMATION":
      return {
        ...state,
        automations: state.automations.map((a) => (a.id === action.id ? { ...a, active: !a.active } : a)),
      };

    case "TEST_AUTOMATION":
      return {
        ...state,
        automations: state.automations.map((a) => (a.id === action.id ? { ...a, tested: true } : a)),
      };

    case "CREATE_ILIA_STAGING": {
      if (state.iliaReleases.stagingId) return state;
      const production = state.iliaReleases.versions.find((version) => version.id === state.iliaReleases.activeId);
      if (!production) return state;
      const number = Math.max(...state.iliaReleases.versions.map((version) => version.number)) + 1;
      const version = {
        ...production,
        id: `ilia-v${number}`,
        number,
        name: `Working copy from Version ${production.number}`,
        summary: "Edit the master prompt and workflows, then test this version before publishing.",
        createdAt: action.at,
        testedAt: null,
        publishedAt: null,
        retiredAt: null,
      };
      return {
        ...state,
        iliaReleases: {
          ...state.iliaReleases,
          stagingId: version.id,
          versions: [version, ...state.iliaReleases.versions].slice(0, 5),
        },
        activity: [logEntry("human", `Created Ilia Version ${number}`, "Staging copy created from production", "ilia-version"), ...state.activity],
      };
    }

    case "TEST_ILIA_RELEASE":
      return {
        ...state,
        iliaReleases: {
          ...state.iliaReleases,
          versions: state.iliaReleases.versions.map((version) => version.id === action.id
            ? { ...version, testedAt: action.at, testSummary: "Passed 8 sample conversations and all required handoffs" }
            : version),
        },
        activity: [logEntry("human", `Tested Ilia Version ${action.number}`, "8 sample conversations passed", "ilia-version"), ...state.activity],
      };

    case "PUBLISH_ILIA_RELEASE": {
      const candidate = state.iliaReleases.versions.find((version) => version.id === action.id);
      if (!candidate?.testedAt || action.id !== state.iliaReleases.stagingId) return state;
      const versions = state.iliaReleases.versions.map((version) => {
        if (version.id === action.id) return { ...version, publishedAt: action.at, retiredAt: null };
        if (version.id === state.iliaReleases.activeId) return { ...version, retiredAt: action.at };
        return version;
      });
      return {
        ...state,
        iliaReleases: { ...state.iliaReleases, activeId: action.id, stagingId: null, versions },
        activity: [logEntry("human", `Published Ilia Version ${candidate.number}`, "Staging promoted to production", "ilia-version"), ...state.activity],
      };
    }

    case "RESTORE_ILIA_RELEASE": {
      const source = state.iliaReleases.versions.find((version) => version.id === action.id);
      if (!source) return state;
      const number = Math.max(...state.iliaReleases.versions.map((version) => version.number)) + 1;
      const version = {
        ...source,
        id: `ilia-v${number}`,
        number,
        name: `Restored from Version ${source.number}`,
        summary: `Restored configuration from Version ${source.number}. Test it before publishing.`,
        createdAt: action.at,
        testedAt: null,
        publishedAt: null,
        retiredAt: null,
      };
      return {
        ...state,
        iliaReleases: {
          ...state.iliaReleases,
          stagingId: version.id,
          versions: [version, ...state.iliaReleases.versions.filter((item) => item.id !== state.iliaReleases.stagingId)].slice(0, 5),
        },
        activity: [logEntry("human", `Restored Ilia Version ${source.number}`, `Created Version ${number} in staging`, "ilia-version"), ...state.activity],
      };
    }

    case "COMPLETE_ACTIVATION":
      return {
        ...state,
        activation: state.activation.map((s) => (s.id === action.id ? { ...s, done: true } : s)),
      };

    case "SET_ERP":
      return {
        ...state,
        erp: { connected: true, name: action.name, lastSync: new Date().toISOString(), autoSync: state.erp?.autoSync || "off", pendingChanges: [] },
        channels: state.channels,
        activity: [logEntry("automation", `Connected ${action.name}`, "ERP source linked for import", "erp"), ...state.activity],
      };

    case "SET_AUTOSYNC":
      return { ...state, erp: { ...state.erp, autoSync: action.freq } };

    case "SYNC_ERP": {      const changes = state.erp.pendingChanges || [];
      const services = changes.length
        ? state.services.map((s) => {
            const ch = changes.find((c) => c.serviceId === s.id);
            return ch ? { ...s, price: ch.to } : s;
          })
        : state.services;
      const activity = changes.length
        ? [logEntry("automation", `Synced ${state.erp.name}`, `${changes.length} price change${changes.length > 1 ? "s" : ""} pulled`, "erp"), ...state.activity]
        : state.activity;
      return {
        ...state,
        services,
        erp: { ...state.erp, lastSync: new Date().toISOString(), pendingChanges: [] },
        activity,
      };
    }

    case "UPDATE_SERVICE":
      return {
        ...state,
        services: state.services.map((s) => (s.id === action.id ? { ...s, ...action.patch } : s)),
      };

    case "SAVE_IMPORTED_DATA": {
      const clinic = { ...state.clinic, ...action.clinic };
      const resourcePatches = new Map((action.resources || []).map((item) => [item.id, item]));
      const servicePatches = new Map((action.services || []).map((item) => [item.id, item]));
      const resources = state.resources.map((resource) => ({ ...resource, ...(resourcePatches.get(resource.id) || {}) }));
      const existingServiceIds = new Set(state.services.map((service) => service.id));
      const services = [
        ...state.services.map((service) => ({ ...service, ...(servicePatches.get(service.id) || {}) })),
        ...(action.services || [])
          .filter((service) => !existingServiceIds.has(service.id))
          .map((service) => ({ ...service, source: service.source || "staff", origin: service.origin || "manual" })),
      ];
      return {
        ...state,
        clinic,
        resources,
        services,
        onboarding: state.onboarding.businessDataReadyAt
          ? {
              ...state.onboarding,
              reviewedAt: action.at,
              knowledgePages: action.knowledgePages || state.onboarding.knowledgePages,
            }
          : state.onboarding,
        activation: state.onboarding.businessDataReadyAt
          ? state.activation.map((item) => item.id === "act-3" ? { ...item, done: true } : item)
          : state.activation,
        erp: { ...state.erp, lastSync: new Date().toISOString() },
        activity: [logEntry("human", `Reviewed ${state.erp?.name || "ERP"} import`, `${services.length} services and ${resources.length} resources checked`, "erp"), ...state.activity],
      };
    }

    case "ADD_SERVICE":
      return { ...state, services: [...state.services, action.service] };

    case "ADD_MANUAL_SERVICE": {
      const service = {
        ...action.service,
        source: "staff",
        origin: "manual",
        createdAt: action.at,
      };
      if (state.services.some((item) => item.id === service.id)) return state;
      return {
        ...state,
        services: [...state.services, service],
        activity: [
          logEntry(
            "human",
            `Added ${service.name}`,
            `${service.duration} minute service at ${service.price} GEL`,
            "service",
          ),
          ...state.activity,
        ],
      };
    }

    // ---- Ilia advances a scripted conversation one beat ----
    case "ILIA_ADVANCE": {
      const conv = state.conversations.find((c) => c.id === action.id);
      if (!conv || !conv.script || conv.scriptStep >= conv.script.length) return state;
      const beat = conv.script[conv.scriptStep];
      let customers = state.customers;
      const offeredSlots = beat.offerSlots
        ? findAvailableSlots({
            bookings: state.bookings,
            services: state.services,
            duties: state.roomDuties,
            serviceId: conv.serviceId,
            count: 3,
          })
        : [];

      const newMsgs = (beat.add || []).map((m) => ({
        id: uid("m"), from: m.from, channel: m.channel || conv.channel,
        text: m.text, ts: new Date().toISOString(), status: "delivered",
        ...(beat.offerSlots && m.from === "ilia"
          ? { slotOptions: offeredSlots, serviceId: conv.serviceId }
          : {}),
      }));
      const newCaptured = beat.capture ? [...conv.captured, ...beat.capture] : conv.captured;
      const waitingOn = beat.add && beat.add[beat.add.length - 1]?.from === "customer" ? "team" : "customer";

      const conversations = state.conversations.map((c) =>
        c.id === conv.id
          ? { ...c, messages: [...c.messages, ...newMsgs], captured: newCaptured,
              scriptStep: c.scriptStep + 1, waitingOn,
              unread: false, lastActivity: new Date().toISOString() }
          : c
      );

      // reflect qualification onto customer record
      if (beat.capture) {
        customers = customers.map((c) =>
          c.id === conv.customerId
            ? { ...c, qualification: [...c.qualification, ...beat.capture.map((f) => ({ ...f }))],
                stage: c.stage === "New" ? "Qualifying" : c.stage }
            : c
        );
      }

      return { ...state, conversations, customers };
    }

    case "TAKE_OVER": {
      const conv = state.conversations.find((c) => c.id === action.id);
      if (!conv) return state;
      const sys = { id: uid("m"), from: "system", text: `${state.user.name} took over. Ilia has paused customer replies.`, ts: new Date().toISOString(), status: "read" };
      return {
        ...state,
        conversations: state.conversations.map((c) =>
          c.id === action.id ? { ...c, lifecycle: "human", owner: state.user.name, waitingOn: "team", unread: false, messages: [...c.messages, sys] } : c
        ),
        activity: [logEntry("human", `Took over ${nameOf(state, conv.customerId)}`, "Ilia paused customer-facing replies", conv.id), ...state.activity],
      };
    }

    case "SEND_HUMAN_MESSAGE": {
      const conv = state.conversations.find((c) => c.id === action.id);
      if (!conv) return state;
      const msg = { id: uid("m"), from: "human", channel: conv.channel, text: action.text, ts: new Date().toISOString(), status: "sent", author: state.user.name };
      return {
        ...state,
        conversations: state.conversations.map((c) =>
          c.id === action.id ? { ...c, messages: [...c.messages, msg], draft: null, waitingOn: "customer", lastActivity: new Date().toISOString() } : c
        ),
        activity: [logEntry("human", `Replied to ${nameOf(state, conv.customerId)}`, action.text.slice(0, 60) + (action.text.length > 60 ? "\u2026" : ""), conv.id), ...state.activity],
      };
    }

    case "SEND_TIME_OPTIONS": {
      const conv = state.conversations.find((c) => c.id === action.id);
      const service = state.services.find((item) => item.id === action.serviceId);
      if (!conv || !service || !action.slots?.length) return state;
      const msg = {
        id: uid("m"),
        from: "human",
        channel: conv.channel,
        text: action.text || `Here are three available times for ${service.name}. Choose the one that works best.`,
        ts: new Date().toISOString(),
        status: "sent",
        author: state.user.name,
        serviceId: service.id,
        bookingId: action.bookingId || null,
        slotOptions: action.slots,
      };
      return {
        ...state,
        conversations: state.conversations.map((item) => item.id === conv.id
          ? { ...item, messages: [...item.messages, msg], draft: null, waitingOn: "customer", lastActivity: msg.ts }
          : item),
        activity: [logEntry("human", `Offered times to ${nameOf(state, conv.customerId)}`, `${service.name}, ${action.slots.length} choices`, conv.id), ...state.activity],
      };
    }

    case "SELECT_CONVERSATION_SLOT": {
      const conv = state.conversations.find((item) => item.id === action.id);
      const offer = conv?.messages.find((message) => message.id === action.messageId);
      const slot = offer?.slotOptions?.find((item) => item.id === action.slotId);
      const service = state.services.find((item) => item.id === (slot?.serviceId || offer?.serviceId));
      if (!conv || !offer || !slot || !service) return state;
      if (bookingAvailabilityIssue(state, { ...slot, serviceId: service.id }, offer.bookingId || null)) return state;
      const now = new Date().toISOString();
      const label = slotLabel(slot.start);
      const humanOffer = offer.from === "human";
      const customerMessage = {
        id: uid("m"), from: "customer", channel: conv.channel,
        text: `${label} works for me.`, ts: now, status: "read",
      };
      const proposalMessage = {
        id: uid("m"), from: humanOffer ? "human" : "ilia", channel: conv.channel,
        text: humanOffer
          ? `Selected: ${label}. Review the choice and confirm the booking.`
          : `Great choice. I have not booked it yet. Please confirm ${service.name} for ${label}.`,
        ts: now, status: humanOffer ? "sent" : "delivered",
        ...(humanOffer ? { author: state.user.name } : {}),
        bookingProposal: slot,
        serviceId: service.id,
      };
      return {
        ...state,
        conversations: state.conversations.map((item) => item.id === conv.id
          ? {
              ...item,
              messages: [
                ...item.messages.map((message) => message.id === offer.id ? { ...message, selectedSlotId: slot.id } : message),
                customerMessage,
                proposalMessage,
              ],
              pendingSlot: {
                ...slot,
                actor: humanOffer ? "human" : "ilia",
                existingBookingId: offer.bookingId || null,
                offerMessageId: offer.id,
              },
              waitingOn: humanOffer ? "team" : "customer",
              lastActivity: now,
            }
          : item),
      };
    }

    case "CONFIRM_CONVERSATION_SLOT": {
      const conv = state.conversations.find((item) => item.id === action.id);
      const pending = conv?.pendingSlot;
      const service = state.services.find((item) => item.id === pending?.serviceId);
      if (!conv || !pending || !service) return state;
      const customer = state.customers.find((item) => item.id === conv.customerId);
      const existing = pending.existingBookingId
        ? state.bookings.find((item) => item.id === pending.existingBookingId)
        : null;
      const bookingId = existing?.id || uid("bk");
      const source = pending.actor === "human" ? "human" : "ilia";
      const booking = snapshotBookingAssignments({
        ...(existing || {}),
        id: bookingId,
        customerId: conv.customerId,
        serviceId: service.id,
        resourceId: pending.resourceId !== undefined ? pending.resourceId : existing?.resourceId,
        resourceIds: pending.resourceIds !== undefined ? pending.resourceIds : existing?.resourceIds,
        ...(pending.doctorId !== undefined || hasOwn(existing, "doctorId")
          ? { doctorId: pending.doctorId !== undefined ? pending.doctorId : existing?.doctorId }
          : {}),
        ...(pending.roomId !== undefined || hasOwn(existing, "roomId")
          ? { roomId: pending.roomId !== undefined ? pending.roomId : existing?.roomId }
          : {}),
        start: pending.start,
        end: pending.end,
        buffer: pending.buffer,
        state: "confirmed",
        source,
        price: service.price,
        priceSource: service.source,
        risk: source === "ilia" ? 8 : 0,
        showUpProbability: existing?.showUpProbability ?? (source === "ilia" ? 92 : 84),
        showUpSource: existing?.showUpSource || "prediction",
        ...(existing ? { lastAction: "rescheduled" } : {}),
      }, state.services, state.resources);
      if (bookingAvailabilityIssue(state, booking, existing?.id || null)) return state;
      const now = new Date().toISOString();
      const label = slotLabel(pending.start);
      const confirmationMessages = [
        ...(source === "ilia" ? [{
          id: uid("m"), from: "customer", channel: conv.channel,
          text: "Yes, confirm it.", ts: now, status: "read",
        }] : []),
        {
          id: uid("m"), from: source, channel: conv.channel,
          text: `Confirmed for ${label}. The booked value is \u20BE${Number(service.price).toLocaleString("en-US")}.`,
          ts: now, status: source === "human" ? "sent" : "delivered",
          ...(source === "human" ? { author: state.user.name } : {}),
        },
      ];
      const bookings = existing
        ? state.bookings.map((item) => item.id === existing.id ? booking : item)
        : [...state.bookings, booking];
      const activity = [logEntry(
        source,
        `${existing ? "Rescheduled" : "Booked"} ${customer?.name || "customer"}`,
        `${service.name}, ${label}`,
        bookingId,
        existing ? {
          undoable: true,
          prevBooking: {
            start: existing.start,
            end: existing.end,
            resourceId: existing.resourceId,
            doctorId: existing.doctorId,
            roomId: existing.roomId,
            resourceIds: existing.resourceIds,
            state: existing.state,
          },
        } : {}
      ), ...state.activity];
      return {
        ...state,
        bookings,
        customers: state.customers.map((item) => item.id === conv.customerId
          ? { ...item, stage: "Booked/Won", owner: source === "ilia" ? "Ilia" : state.user.name, nextAction: null }
          : item),
        conversations: state.conversations.map((item) => item.id === conv.id
          ? {
              ...item,
              messages: [
                ...item.messages.map((message) => message.id === pending.offerMessageId
                  ? { ...message, confirmedSlotId: pending.id }
                  : message),
                ...confirmationMessages,
              ],
              bookingId,
              pendingSlot: null,
              lifecycle: "resolved",
              waitingOn: null,
              unread: false,
              lastActivity: now,
            }
          : item),
        activity,
      };
    }

    case "UPDATE_DRAFT":
      return {
        ...state,
        conversations: state.conversations.map((c) => (c.id === action.id ? { ...c, draft: action.text } : c)),
      };

    case "RETURN_TO_ILIA": {
      const conv = state.conversations.find((c) => c.id === action.id);
      if (!conv) return state;
      const sys = { id: uid("m"), from: "system", text: "Ownership returned to Ilia.", ts: new Date().toISOString(), status: "read" };
      return {
        ...state,
        conversations: state.conversations.map((c) =>
          c.id === action.id ? { ...c, lifecycle: "ilia", owner: "Ilia", waitingOn: "customer", messages: [...c.messages, sys] } : c
        ),
        activity: [logEntry("human", `Returned ${nameOf(state, conv.customerId)} to Ilia`, "", conv.id), ...state.activity],
      };
    }

    case "RESOLVE_CONV": {
      const conv = state.conversations.find((c) => c.id === action.id);
      if (!conv) return state;
      return {
        ...state,
        conversations: state.conversations.map((c) => (c.id === action.id ? { ...c, lifecycle: "resolved", waitingOn: null, unread: false } : c)),
        activity: [logEntry(conv.owner === "Ilia" ? "ilia" : "human", `Resolved ${nameOf(state, conv.customerId)}`, "", conv.id), ...state.activity],
      };
    }

    case "SET_CONVERSATION_ARCHIVED": {
      const conv = state.conversations.find((c) => c.id === action.id);
      if (!conv) return state;
      const archivedAt = action.archived ? action.at : null;
      return {
        ...state,
        conversations: state.conversations.map((c) =>
          c.id === action.id
            ? {
                ...c,
                archivedAt,
                archivedBy: action.archived ? state.user.name : null,
                unread: action.archived ? false : c.unread,
              }
            : c
        ),
        activity: [
          logEntry(
            "human",
            `${action.archived ? "Archived" : "Restored"} ${nameOf(state, conv.customerId)}`,
            "",
            conv.id,
          ),
          ...state.activity,
        ],
      };
    }

    case "SNOOZE":
      return {
        ...state,
        conversations: state.conversations.map((c) => (c.id === action.id ? { ...c, snoozedUntil: action.until, unread: false } : c)),
      };

    case "TOGGLE_UNREAD":
      return {
        ...state,
        conversations: state.conversations.map((c) => (c.id === action.id ? { ...c, unread: action.value } : c)),
      };

    case "ADD_CUSTOMER":
      if (!action.customer?.id || state.customers.some((customer) => customer.id === action.customer.id)) return state;
      return {
        ...state,
        customers: [action.customer, ...state.customers],
        activity: [logEntry("human", `Created ${action.customer.name}`, "Customer profile created while scheduling", action.customer.id), ...state.activity],
      };

    case "ADD_NOTE":
      return {
        ...state,
        customers: state.customers.map((c) =>
          c.id === action.customerId
            ? { ...c, notes: [{ id: uid("n"), by: state.user.name, ts: new Date().toISOString(), text: action.text }, ...c.notes] }
            : c
        ),
      };

    case "SET_STAGE": {
      const cust = state.customers.find((c) => c.id === action.customerId);
      if (!cust) return state;
      return {
        ...state,
        customers: state.customers.map((c) =>
          c.id === action.customerId ? { ...c, stage: action.stage, closeReason: action.reason || null } : c
        ),
        activity: [logEntry("human", `Moved ${cust.name} to ${action.stage}`, action.reason || "", cust.id), ...state.activity],
      };
    }

    case "SET_NEXT_ACTION":
      return {
        ...state,
        customers: state.customers.map((c) =>
          c.id === action.customerId ? { ...c, nextAction: action.nextAction } : c
        ),
      };

    case "REFRESH_SEGMENTS": {
      const segments = action.segments || refreshSegments(state, state.segments || []);
      const refreshedAt = new Date().toISOString();
      return {
        ...state,
        segments,
        segmentSync: {
          ...(state.segmentSync || {}),
          crm: state.erp?.name || state.segmentSync?.crm || "CRM",
          connected: Boolean(state.erp?.connected),
          lastPullAt: state.segmentSync?.lastPullAt || refreshedAt,
          lastAnalysisAt: refreshedAt,
          recordsChecked: state.customers.length,
          conversationsChecked: state.conversations.length,
          pendingReview: state.customers.filter((customer) => customer.mergePending).length,
        },
        activity: [logEntry("ilia", "Refreshed customer segments", action.detail || `${segments.length} segments updated from conversations and CRM fields`, "segments"), ...state.activity],
      };
    }

    case "ADD_SEGMENT":
      return {
        ...state,
        segments: [...(state.segments || []), action.segment],
        activity: [logEntry("human", `Created segment ${action.segment.name}`, `${action.segment.memberIds.length} people assigned`, action.segment.id), ...state.activity],
      };

    case "SET_CUSTOMER_SEGMENTS": {
      const selected = new Set(action.segmentIds);
      const segments = (state.segments || []).map((segment) => {
        if (segment.membershipLocked) return segment;
        const isMember = segment.memberIds.includes(action.customerId);
        const shouldBeMember = selected.has(segment.id);
        if (isMember === shouldBeMember) return segment;

        if (segment.source === "staff") {
          const memberIds = shouldBeMember
            ? Array.from(new Set([...segment.memberIds, action.customerId]))
            : segment.memberIds.filter((id) => id !== action.customerId);
          return {
            ...segment,
            memberIds,
            manualIncludeIds: memberIds,
            manualExcludeIds: [],
            memberEvidence: {
              ...segment.memberEvidence,
              ...(shouldBeMember ? { [action.customerId]: "Added by a team member" } : {}),
            },
          };
        }

        return {
          ...segment,
          memberIds: shouldBeMember
            ? Array.from(new Set([...segment.memberIds, action.customerId]))
            : segment.memberIds.filter((id) => id !== action.customerId),
          manualIncludeIds: shouldBeMember
            ? Array.from(new Set([...(segment.manualIncludeIds || []), action.customerId]))
            : (segment.manualIncludeIds || []).filter((id) => id !== action.customerId),
          manualExcludeIds: shouldBeMember
            ? (segment.manualExcludeIds || []).filter((id) => id !== action.customerId)
            : Array.from(new Set([...(segment.manualExcludeIds || []), action.customerId])),
          memberEvidence: {
            ...segment.memberEvidence,
            ...(shouldBeMember ? { [action.customerId]: "Added by a team member" } : {}),
          },
        };
      });
      return {
        ...state,
        segments,
        activity: [logEntry("human", `Updated segments for ${nameOf(state, action.customerId)}`, `${selected.size} memberships selected`, action.customerId), ...state.activity],
      };
    }

    case "SET_SEGMENT_MEMBERS": {
      const selected = new Set(action.memberIds);
      const segments = (state.segments || []).map((segment) => {
        if (segment.id !== action.segmentId) return segment;
        if (segment.membershipLocked) return segment;
        if (segment.source === "staff") {
          return {
            ...segment,
            memberIds: action.memberIds,
            manualIncludeIds: action.memberIds,
            manualExcludeIds: [],
            memberEvidence: Object.fromEntries(action.memberIds.map((customerId) => [customerId, segment.memberEvidence?.[customerId] || "Added by a team member"])),
          };
        }

        const manualIncludeIds = new Set(segment.manualIncludeIds || []);
        const manualExcludeIds = new Set(segment.manualExcludeIds || []);
        state.customers.forEach((customer) => {
          const isMember = segment.memberIds.includes(customer.id);
          const shouldBeMember = selected.has(customer.id);
          if (isMember === shouldBeMember) return;
          if (shouldBeMember) {
            manualIncludeIds.add(customer.id);
            manualExcludeIds.delete(customer.id);
          } else {
            manualIncludeIds.delete(customer.id);
            manualExcludeIds.add(customer.id);
          }
        });
        return {
          ...segment,
          memberIds: action.memberIds,
          manualIncludeIds: Array.from(manualIncludeIds),
          manualExcludeIds: Array.from(manualExcludeIds),
          memberEvidence: {
            ...segment.memberEvidence,
            ...Object.fromEntries(action.memberIds.filter((customerId) => !segment.memberEvidence?.[customerId]).map((customerId) => [customerId, "Added by a team member"])),
          },
        };
      });
      const segment = segments.find((item) => item.id === action.segmentId);
      return {
        ...state,
        segments,
        activity: [logEntry("human", `Updated ${segment?.name || "segment"}`, `${action.memberIds.length} people assigned`, action.segmentId), ...state.activity],
      };
    }

    // ---- Calendar actions ----
    case "ADD_BOOKING": {
      const booking = snapshotBookingAssignments({
        ...action.booking,
        showUpProbability: action.booking?.showUpProbability ?? 84,
        showUpSource: action.booking?.showUpSource || "prediction",
      }, state.services, state.resources);
      if (!booking || state.bookings.some((item) => item.id === booking.id)) return state;
      if (bookingAvailabilityIssue(state, booking)) return state;
      const customer = state.customers.find((item) => item.id === booking.customerId);
      const service = state.services.find((item) => item.id === booking.serviceId);
      const openConversation = state.conversations
        .filter((conversation) => conversation.customerId === booking.customerId && !conversation.bookingId && conversation.lifecycle !== "resolved")
        .sort((a, b) => new Date(b.lastActivity) - new Date(a.lastActivity))[0];
      const now = new Date().toISOString();
      const isConfirmed = booking.state === "confirmed";
      const conversations = openConversation
        ? state.conversations.map((conversation) => conversation.id === openConversation.id
          ? {
              ...conversation,
              bookingId: booking.id,
              lifecycle: isConfirmed ? "resolved" : "waiting_customer",
              waitingOn: isConfirmed ? null : "customer",
              lastActivity: now,
              messages: action.notify
                ? [...conversation.messages, {
                    id: uid("m"), from: "human", channel: conversation.channel, text: action.notify,
                    ts: now, status: "sent", author: state.user.name,
                  }]
                : conversation.messages,
            }
          : conversation)
        : state.conversations;
      const customers = state.customers.map((item) => item.id === booking.customerId
        ? {
            ...item,
            stage: isConfirmed ? "Booked/Won" : item.stage,
            owner: item.owner || state.user.name,
            nextAction: isConfirmed ? null : {
              label: "Confirm pending booking",
              due: "today",
              owner: state.user.name,
            },
          }
        : item);
      const detail = `${service?.name || "Appointment"} · \u20BE${Number(booking.price || 0).toLocaleString("en-US")}`;
      const activityLabel = booking.state === "confirmed"
        ? `Booked ${customer?.name || "customer"}`
        : booking.state === "hold"
          ? `Held a slot for ${customer?.name || "customer"}`
          : `Added a pending booking for ${customer?.name || "customer"}`;
      return {
        ...state,
        bookings: [...state.bookings, booking],
        conversations,
        customers,
        activity: [logEntry("human", activityLabel, detail, booking.id), ...state.activity],
      };
    }

    // Consequential changes keep undo snapshots.
    case "RESCHEDULE_BOOKING": {
      const bk = state.bookings.find((b) => b.id === action.id);
      if (!bk) return state;
      const prev = {
        start: bk.start,
        end: bk.end,
        resourceId: bk.resourceId,
        doctorId: bk.doctorId,
        roomId: bk.roomId,
        resourceIds: bk.resourceIds,
        state: bk.state,
      };
      const nextDoctorId = action.doctorId !== undefined ? action.doctorId : bk.doctorId;
      const nextRoomId = action.roomId !== undefined ? action.roomId : bk.roomId;
      const assignmentChanged = action.doctorId !== undefined || action.roomId !== undefined;
      const extraResourceIds = (bk.resourceIds || [])
        .filter((id) => id !== bk.doctorId && id !== bk.roomId);
      const nextResourceIds = action.resourceIds !== undefined
        ? action.resourceIds
        : assignmentChanged
          ? [...extraResourceIds, nextDoctorId, nextRoomId].filter(Boolean)
          : bk.resourceIds;
      const updatedBooking = snapshotBookingAssignments({
        ...bk,
        start: action.start !== undefined ? action.start : bk.start,
        end: action.end !== undefined ? action.end : bk.end,
        resourceId: action.resourceId !== undefined
          ? action.resourceId
          : assignmentChanged
            ? nextDoctorId || nextRoomId || null
            : bk.resourceId,
        doctorId: nextDoctorId,
        roomId: nextRoomId,
        resourceIds: nextResourceIds,
        state: "confirmed",
        lastAction: "rescheduled",
      }, state.services, state.resources);
      if (bookingAvailabilityIssue(state, updatedBooking, action.id)) return state;
      const bookings = state.bookings.map((b) => b.id === action.id ? updatedBooking : b);
      const act = logEntry(action.actor || "human", `Rescheduled ${nameOf(state, bk.customerId)}`, action.label || "", bk.id, { undoable: true, prevBooking: prev });
      // resolve the linked conversation if any
      const conversations = state.conversations.map((c) =>
        c.bookingId === action.id && c.journey === "reschedule"
          ? { ...c, lifecycle: "resolved", waitingOn: "customer",
              messages: [...c.messages, { id: uid("m"), from: "human", channel: c.channel, text: action.notify || "Done \u2014 I\u2019ve moved your appointment. You\u2019ll get a fresh confirmation.", ts: new Date().toISOString(), status: "sent", author: state.user.name }] }
          : c
      );
      return { ...state, bookings, conversations, activity: [act, ...state.activity] };
    }

    case "CANCEL_BOOKING": {
      const bk = state.bookings.find((b) => b.id === action.id);
      if (!bk) return state;
      const prev = { state: bk.state };
      const bookings = state.bookings.map((b) => (b.id === action.id ? { ...b, state: "cancelled" } : b));
      const act = logEntry(action.actor || "human", `Cancelled ${nameOf(state, bk.customerId)}`, action.label || "", bk.id, { undoable: true, prevBooking: prev });
      return { ...state, bookings, activity: [act, ...state.activity] };
    }

    case "MARK_BOOKING": {
      const bk = state.bookings.find((b) => b.id === action.id);
      if (!bk) return state;
      const prev = { state: bk.state };
      const bookings = state.bookings.map((b) => (b.id === action.id ? { ...b, state: action.state } : b));
      const label = action.state === "completed" ? "Marked attended" : action.state === "noshow" ? "Marked no-show" : `Set ${action.state}`;
      const act = logEntry("human", `${label}: ${nameOf(state, bk.customerId)}`, "", bk.id, { undoable: true, prevBooking: prev });
      return { ...state, bookings, activity: [act, ...state.activity] };
    }

    case "UNDO": {
      const entry = state.activity.find((a) => a.id === action.id);
      if (!entry || !entry.prevBooking) return state;
      const bookings = state.bookings.map((b) => (b.id === entry.entity ? { ...b, ...entry.prevBooking } : b));
      return {
        ...state,
        bookings,
        activity: state.activity.map((a) => (a.id === action.id ? { ...a, undoable: false, undone: true, action: a.action + " (undone)" } : a)),
      };
    }

    case "UNDO_LAST": {
      const entry = state.activity.find((a) => a.undoable && a.prevBooking && !a.undone);
      if (!entry) return state;
      const bookings = state.bookings.map((b) => (b.id === entry.entity ? { ...b, ...entry.prevBooking } : b));
      return {
        ...state,
        bookings,
        activity: state.activity.map((a) => (a.id === entry.id ? { ...a, undoable: false, undone: true, action: a.action + " (undone)" } : a)),
      };
    }

    default:
      return state;
  }
}

function nameOf(state, customerId) {
  return state.customers.find((c) => c.id === customerId)?.name || "customer";
}

const StoreCtx = createContext(null);

export function StoreProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, undefined, loadState);
  setRuntimeLanguage(state.lang);
  const tr = (key, params = {}) => translateKey(key, params, state.lang);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (e) { /* ignore */ }
  }, [state]);

  // convenience action creators with toasts / undo affordances
  const actions = {
    dispatch,
    setLang: (lang) => dispatch({ type: "SET_LANG", lang }),
    reset: () => {
      dispatch({ type: "RESET" });
      toast.success(translateKey("toast.prototypeReset", {}, state.lang));
    },
    iliaAdvance: (id) => dispatch({ type: "ILIA_ADVANCE", id }),
    takeOver: (id) => { dispatch({ type: "TAKE_OVER", id }); toast.success(tr("toast.takeover")); },
    sendHuman: (id, text) => dispatch({ type: "SEND_HUMAN_MESSAGE", id, text }),
    sendTimeOptions: (id, { slots, serviceId, bookingId = null, text = null }) => {
      if (!slots?.length || !serviceId) {
        toast.error(tr("toast.times.noneTitle"), { description: tr("toast.times.noneDescription") });
        return false;
      }
      const unavailable = slots
        .map((slot) => bookingAvailabilityIssue(
          state,
          { ...slot, serviceId: slot.serviceId || serviceId },
          bookingId,
        ))
        .find(Boolean);
      if (unavailable) {
        toast.error(tr("toast.times.changedTitle"), { description: unavailable.description });
        return false;
      }
      dispatch({ type: "SEND_TIME_OPTIONS", id, slots, serviceId, bookingId, text });
      toast.success(tr(slots.length === 1 ? "toast.times.sentTitle" : "toast.times.sentTitlePlural", { count: slots.length }), {
        description: tr("toast.times.sentDescription"),
      });
      return true;
    },
    selectConversationSlot: (id, messageId, slotId) => {
      const conv = state.conversations.find((item) => item.id === id);
      const offer = conv?.messages.find((message) => message.id === messageId);
      const slot = offer?.slotOptions?.find((item) => item.id === slotId);
      const issue = slot
        ? bookingAvailabilityIssue(state, { ...slot, serviceId: slot.serviceId || offer.serviceId }, offer.bookingId || null)
        : null;
      if (!slot || issue) {
        toast.error(tr("toast.times.unavailableTitle"), {
          description: issue?.description || tr("toast.times.refreshChoices"),
        });
        return false;
      }
      dispatch({ type: "SELECT_CONVERSATION_SLOT", id, messageId, slotId });
      return true;
    },
    confirmConversationSlot: (id) => {
      const conv = state.conversations.find((item) => item.id === id);
      const pending = conv?.pendingSlot;
      const issue = pending
        ? bookingAvailabilityIssue(state, pending, pending.existingBookingId || null)
        : null;
      if (!pending || issue) {
        toast.error(tr("toast.times.unavailableTitle"), {
          description: issue?.description || tr("toast.times.offerNew"),
        });
        return false;
      }
      dispatch({ type: "CONFIRM_CONVERSATION_SLOT", id });
      toast.success(tr(pending.existingBookingId ? "toast.booking.rescheduled" : "toast.booking.confirmed"), {
        description: tr("toast.booking.conversationMatched"),
      });
      return true;
    },
    updateDraft: (id, text) => dispatch({ type: "UPDATE_DRAFT", id, text }),
    returnToIlia: (id) => { dispatch({ type: "RETURN_TO_ILIA", id }); toast.success(tr("toast.conversation.returnedToIlia")); },
    resolve: (id) => { dispatch({ type: "RESOLVE_CONV", id }); toast.success(tr("toast.conversation.resolved")); },
    archiveConversation: (id) => {
      dispatch({ type: "SET_CONVERSATION_ARCHIVED", id, archived: true, at: new Date().toISOString() });
      toast.success(tr("toast.conversation.archived"));
    },
    restoreConversation: (id) => {
      dispatch({ type: "SET_CONVERSATION_ARCHIVED", id, archived: false, at: new Date().toISOString() });
      toast.success(tr("toast.conversation.restored"));
    },
    snooze: (id, until) => {
      dispatch({ type: "SNOOZE", id, until });
      toast(tr("toast.conversation.snoozed"), { description: tr("toast.conversation.snoozedDescription") });
    },
    toggleUnread: (id, value) => dispatch({ type: "TOGGLE_UNREAD", id, value }),
    addNote: (customerId, text) => dispatch({ type: "ADD_NOTE", customerId, text }),
    addCustomer: (fields) => {
      const id = uid("cust");
      const name = fields.name.trim();
      const value = Number(fields.value || 0);
      const customer = {
        id,
        name,
        initials: name.split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase(),
        lang: "ka",
        ageBand: null,
        channels: [],
        phone: fields.phone.trim(),
        email: fields.email.trim(),
        consent: { messaging: false, marketing: false, updated: new Date().toISOString() },
        stage: "New",
        owner: state.user.name,
        value,
        valueSource: "staff",
        valueBasis: value > 0 ? "Entered when customer profile was created" : "Not qualified yet",
        source: "Staff",
        lastInteraction: new Date().toISOString(),
        nextAction: null,
        insurance: null,
        first: true,
        history: { visits: 0, noShows: 0, moved: 0 },
        commercial: { spent: 0, openValue: value, lifetimeValue: value, lifetimeBasis: "No repeat-visit history yet." },
        qualification: [],
        notes: [],
      };
      dispatch({ type: "ADD_CUSTOMER", customer });
      toast.success(tr("toast.customer.created"), {
        description: tr("toast.customer.selectedForBooking", { name }),
      });
      return id;
    },
    setStage: (customerId, stage, reason) => dispatch({ type: "SET_STAGE", customerId, stage, reason }),
    setNextAction: (customerId, nextAction) => dispatch({ type: "SET_NEXT_ACTION", customerId, nextAction }),
    refreshSegments: () => {
      const current = state.segments || [];
      const segments = refreshSegments(state, current);
      const priorMemberships = new Set(current.flatMap((segment) => segment.memberIds.map((customerId) => `${segment.id}:${customerId}`)));
      const nextMemberships = new Set(segments.flatMap((segment) => segment.memberIds.map((customerId) => `${segment.id}:${customerId}`)));
      const newAssignments = segments.reduce((total, segment) => total + segment.memberIds.filter((customerId) => !priorMemberships.has(`${segment.id}:${customerId}`)).length, 0);
      const removedAssignments = Array.from(priorMemberships).filter((membership) => !nextMemberships.has(membership)).length;
      const pendingReview = state.customers.filter((customer) => customer.mergePending).length;
      const detail = tr("toast.segments.refreshDetail", {
        records: state.customers.length,
        added: newAssignments,
        removed: removedAssignments,
        review: pendingReview,
      });
      dispatch({ type: "REFRESH_SEGMENTS", segments, detail });
      toast.success(tr("toast.segments.refreshed"), {
        description: tr("toast.segments.refreshDescription", { detail }),
      });
    },
    addSegment: ({ name, category, description, memberIds }) => {
      const id = uid("seg");
      dispatch({
        type: "ADD_SEGMENT",
        segment: {
          id,
          name,
          category,
          description,
          source: "staff",
          memberIds,
          memberEvidence: Object.fromEntries(memberIds.map((customerId) => [customerId, "Added by a team member"])),
          manualIncludeIds: memberIds,
          manualExcludeIds: [],
          lastRefreshed: new Date().toISOString(),
        },
      });
      toast.success(tr("toast.segments.manualCreated"));
      return id;
    },
    setCustomerSegments: (customerId, segmentIds) => {
      dispatch({ type: "SET_CUSTOMER_SEGMENTS", customerId, segmentIds });
      toast.success(tr("toast.segments.membershipsUpdated"), {
        description: tr("toast.segments.manualChoicesPersist"),
      });
    },
    setSegmentMembers: (segmentId, memberIds) => {
      dispatch({ type: "SET_SEGMENT_MEMBERS", segmentId, memberIds });
      toast.success(tr("toast.segments.membershipUpdated"), {
        description: tr("toast.segments.manualChoicesPersist"),
      });
    },
    resetOnboarding: () => dispatch({ type: "RESET_ONBOARDING" }),
    connectOnboardingSource: (source) => dispatch({ type: "CONNECT_ONBOARDING_SOURCE", source, at: new Date().toISOString() }),
    startOnboardingImport: (websiteUrl) => dispatch({ type: "START_ONBOARDING_IMPORT", websiteUrl, at: new Date().toISOString() }),
    connectOnboardingChannel: (channel) => dispatch({
      type: "CONNECT_ONBOARDING_CHANNEL",
      channelId: channel.id,
      channelName: channel.name,
      channelHandle: channel.handle,
    }),
    finishOnboardingImport: () => dispatch({ type: "FINISH_ONBOARDING_IMPORT", at: new Date().toISOString() }),
    updateOnboarding: (patch) => dispatch({ type: "UPDATE_ONBOARDING", patch }),
    connectChannel: (id) => { dispatch({ type: "CONNECT_CHANNEL", id }); toast.success(tr("toast.channel.reconnected")); },
    toggleAutomation: (id) => dispatch({ type: "TOGGLE_AUTOMATION", id }),
    testAutomation: (id) => { dispatch({ type: "TEST_AUTOMATION", id }); toast.success(tr("toast.automation.testPassed")); },
    createIliaStaging: () => {
      if (state.iliaReleases.stagingId) {
        toast(tr("toast.release.stagingExists"), { description: tr("toast.release.stagingExistsDescription") });
        return false;
      }
      dispatch({ type: "CREATE_ILIA_STAGING", at: new Date().toISOString() });
      toast.success(tr("toast.release.stagingCreated"));
      return true;
    },
    testIliaRelease: (id) => {
      const version = state.iliaReleases.versions.find((item) => item.id === id);
      if (!version || id !== state.iliaReleases.stagingId) return false;
      dispatch({ type: "TEST_ILIA_RELEASE", id, number: version.number, at: new Date().toISOString() });
      toast.success(tr("toast.release.testPassed", { version: version.number }), {
        description: tr("toast.release.testPassedDescription"),
      });
      return true;
    },
    publishIliaRelease: (id) => {
      const version = state.iliaReleases.versions.find((item) => item.id === id);
      if (!version?.testedAt) {
        toast.error(tr("toast.release.testBeforePublish"), { description: tr("toast.release.productionUnchanged") });
        return false;
      }
      dispatch({ type: "PUBLISH_ILIA_RELEASE", id, at: new Date().toISOString() });
      toast.success(tr("toast.release.published", { version: version.number }), {
        description: tr("toast.release.historyAvailable"),
      });
      return true;
    },
    restoreIliaRelease: (id) => {
      const version = state.iliaReleases.versions.find((item) => item.id === id);
      if (!version) return false;
      dispatch({ type: "RESTORE_ILIA_RELEASE", id, at: new Date().toISOString() });
      toast.success(tr("toast.release.restored", { version: version.number }), {
        description: tr("toast.release.testRestored"),
      });
      return true;
    },
    completeActivation: (id) => dispatch({ type: "COMPLETE_ACTIVATION", id }),
    setErp: (name) => dispatch({ type: "SET_ERP", name }),
    syncErp: () => dispatch({ type: "SYNC_ERP" }),
    setAutoSync: (freq) => dispatch({ type: "SET_AUTOSYNC", freq }),
    updateService: (id, patch) => dispatch({ type: "UPDATE_SERVICE", id, patch }),
    saveImportedData: (payload) => {
      dispatch({ type: "SAVE_IMPORTED_DATA", ...payload, at: new Date().toISOString() });
      toast.success(tr("toast.import.saved"), { description: tr("toast.import.savedDescription") });
    },
    addService: (service) => dispatch({ type: "ADD_SERVICE", service }),
    addManualService: (fields) => {
      const service = {
        id: uid("s-manual"),
        name: fields.name.trim(),
        price: Number(fields.price),
        duration: Number(fields.duration),
        buffer: Number(fields.buffer),
        resource: fields.resource,
        room: fields.room || null,
        description: fields.description.trim(),
      };
      dispatch({ type: "ADD_MANUAL_SERVICE", service, at: new Date().toISOString() });
      toast.success(tr("toast.service.added"), { description: tr("toast.service.addedDescription") });
      return { ...service, source: "staff", origin: "manual" };
    },
    addBooking: ({ booking, notify }) => {
      const candidate = snapshotBookingAssignments(booking, state.services, state.resources);
      const issue = bookingAvailabilityIssue(state, candidate);
      if (issue) {
        toast.error(tr("toast.booking.unavailable"), { description: issue.description });
        return false;
      }
      dispatch({ type: "ADD_BOOKING", booking: candidate, notify });
      toast.success(tr("toast.booking.created"), {
        description: tr(notify ? "toast.booking.createdNotified" : "toast.booking.createdDescription"),
      });
      return true;
    },
    reschedule: (payload) => {
      const current = state.bookings.find((booking) => booking.id === payload.id);
      if (!current) {
        toast.error(tr("toast.booking.notFound"), { description: tr("toast.booking.refreshCalendar") });
        return false;
      }
      const nextDoctorId = payload.doctorId !== undefined ? payload.doctorId : current.doctorId;
      const nextRoomId = payload.roomId !== undefined ? payload.roomId : current.roomId;
      const assignmentChanged = payload.doctorId !== undefined || payload.roomId !== undefined;
      const extraResourceIds = (current.resourceIds || [])
        .filter((id) => id !== current.doctorId && id !== current.roomId);
      const nextResourceIds = payload.resourceIds !== undefined
        ? payload.resourceIds
        : assignmentChanged
          ? [...extraResourceIds, nextDoctorId, nextRoomId].filter(Boolean)
          : current.resourceIds;
      const candidate = snapshotBookingAssignments({
        ...current,
        ...payload,
        resourceId: payload.resourceId !== undefined
          ? payload.resourceId
          : assignmentChanged
            ? nextDoctorId || nextRoomId || null
            : current.resourceId,
        doctorId: nextDoctorId,
        roomId: nextRoomId,
        resourceIds: nextResourceIds,
      }, state.services, state.resources);
      const issue = bookingAvailabilityIssue(state, candidate, payload.id);
      if (issue) {
        toast.error(tr("toast.booking.unavailable"), { description: issue.description });
        return false;
      }
      dispatch({
        type: "RESCHEDULE_BOOKING",
        ...payload,
        resourceId: candidate.resourceId,
        doctorId: candidate.doctorId,
        roomId: candidate.roomId,
        resourceIds: candidate.resourceIds,
      });
      toast.success(tr("toast.booking.rescheduled"), {
        description: tr("toast.booking.customerNotified"),
        action: { label: tr("toast.action.undo"), onClick: () => dispatch({ type: "UNDO_LAST" }) },
      });
      return true;
    },
    cancelBooking: (id, label) => {
      dispatch({ type: "CANCEL_BOOKING", id, label });
      toast.success(tr("toast.booking.cancelled"), {
        action: { label: tr("toast.action.undo"), onClick: () => dispatch({ type: "UNDO_LAST" }) },
      });
    },
    markBooking: (id, stateVal) => {
      dispatch({ type: "MARK_BOOKING", id, state: stateVal });
      toast.success(tr(stateVal === "completed"
        ? "toast.booking.markedAttended"
        : stateVal === "noshow"
          ? "toast.booking.markedNoShow"
          : "toast.booking.updated"), {
        action: { label: tr("toast.action.undo"), onClick: () => dispatch({ type: "UNDO_LAST" }) },
      });
    },
    undo: (id) => { dispatch({ type: "UNDO", id }); toast.success(tr("toast.action.undone")); },
  };

  return <StoreCtx.Provider value={{ state, actions, STAGES, CLOSED_STAGES }}>{children}</StoreCtx.Provider>;
}

export function useStore() {
  const ctx = useContext(StoreCtx);
  if (!ctx) throw new Error("useStore must be used within StoreProvider");
  return ctx;
}

// ---- Derived selectors (hooks) ----
export function useCustomer(id) {
  const { state } = useStore();
  return state.customers.find((c) => c.id === id);
}
export function useService(id) {
  const { state } = useStore();
  return state.services.find((s) => s.id === id);
}
export function useResource(id) {
  const { state } = useStore();
  return state.resources.find((r) => r.id === id);
}
